import React, { useState, useEffect, useCallback } from 'react';
import { 
  UserIcon,
  ClockIcon,
  ShoppingBagIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { dbHelpers, supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import DashboardDebug from '../components/DashboardDebug';

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState({
    // Leads Statistics
    totalLeads: 0,
    todayLeads: 0,
    thisMonthLeads: 0,
    thisWeekLeads: 0,
    recentLeads: [],
    leadStatusData: [],
    leadSourceData: [],
    leadsTrendData: [],
    
    // Follow-ups Statistics
    totalFollowUps: 0,
    todayFollowUps: 0,
    overdueFollowUps: 0,
    upcomingFollowUps: 0,
    followUpStatusData: [],
    
    // Orders Statistics
    totalOrders: 0,
    todayOrders: 0,
    thisMonthOrders: 0,
    conversionRate: 0,
    orderStatusData: [],
    ordersTrendData: [],
    
    // Performance Metrics
    avgResponseTime: 0,
    topSources: [],
    monthlyGrowth: 0
  });
  const [loading, setLoading] = useState(true);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading comprehensive dashboard data for user:', user.id);
      console.log('User object:', user);
      
      // Get current date ranges
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Calculate this week start (Monday)
      const thisWeekStart = new Date(now);
      thisWeekStart.setDate(now.getDate() - now.getDay() + 1); // Monday
      thisWeekStart.setHours(0, 0, 0, 0);
      
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      
      // Fetch all data
      const [leads, followUps, orders] = await Promise.all([
        dbHelpers.getLeads(user.id),
        supabase.from('leads').select('*').or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`).not('follow_up_date', 'is', null),
        supabase.from('orders').select('*').eq('user_id', user.id)
      ]);

      // Fallback: if leads is empty, try direct query
      let leadsData = leads || [];
      if (leadsData.length === 0) {
        console.log('No leads from helper, trying direct query...');
        const { data: directLeads, error: directError } = await supabase
          .from('leads')
          .select('*')
          .or(`assigned_to.eq.${user.id},user_id.eq.${user.id}`)
          .order('created_at', { ascending: false });
        
        if (!directError && directLeads) {
          leadsData = directLeads;
          console.log('Direct query returned:', directLeads.length, 'leads');
        } else {
          console.error('Direct query error:', directError);
          
          // Last resort: try to get all leads (for debugging)
          console.log('Trying to get all leads for debugging...');
          const { data: allLeads, error: allError } = await supabase
            .from('leads')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(10);
          
          if (!allError && allLeads) {
            console.log('All leads query returned:', allLeads.length, 'leads');
            console.log('Sample lead:', allLeads[0]);
            
            // If we have leads but no user-specific leads, use all leads for now
            if (allLeads.length > 0) {
              leadsData = allLeads;
              console.log('Using all leads for dashboard display');
            }
          } else {
            console.error('All leads query error:', allError);
          }
        }
      }

      const followUpsData = followUps.data || [];
      const ordersData = orders.data || [];

      console.log('Raw data received:', {
        leads: leads,
        followUps: followUps,
        orders: orders
      });

      console.log('Dashboard data loaded:', {
        leads: leadsData.length,
        followUps: followUpsData.length,
        orders: ordersData.length
      });

      console.log('Date ranges:', {
        today: today.toISOString(),
        thisWeekStart: thisWeekStart.toISOString(),
        thisMonthStart: thisMonthStart.toISOString()
      });

      // Calculate time-based statistics
      const todayLeads = leadsData.filter(lead => new Date(lead.created_at) >= today).length;
      const thisWeekLeads = leadsData.filter(lead => new Date(lead.created_at) >= thisWeekStart).length;
      const thisMonthLeads = leadsData.filter(lead => new Date(lead.created_at) >= thisMonthStart).length;
      
      console.log('Calculated statistics:', {
        todayLeads,
        thisWeekLeads,
        thisMonthLeads,
        totalLeads: leadsData.length
      });
      
      const todayOrders = ordersData.filter(order => new Date(order.conversion_date) >= today).length;
      const thisMonthOrders = ordersData.filter(order => new Date(order.conversion_date) >= thisMonthStart).length;
      
      const todayFollowUps = followUpsData.filter(fu => {
        const followUpDate = new Date(fu.follow_up_date);
        return followUpDate.toDateString() === today.toDateString();
      }).length;
      
      const overdueFollowUps = followUpsData.filter(fu => new Date(fu.follow_up_date) < today).length;
      const upcomingFollowUps = followUpsData.filter(fu => new Date(fu.follow_up_date) > today).length;

      // Calculate conversion rate
      const conversionRate = leadsData.length > 0 ? (ordersData.length / leadsData.length * 100) : 0;

      // Lead status data
      const leadStatusCount = leadsData.reduce((acc, lead) => {
        acc[lead.status] = (acc[lead.status] || 0) + 1;
        return acc;
      }, {});

      const leadStatusData = Object.entries(leadStatusCount).map(([status, count]) => ({
        name: status.replace('_', ' ').toUpperCase(),
        value: count
      }));

      // Lead source data
      const leadSourceCount = leadsData.reduce((acc, lead) => {
        const source = lead.source || 'Unknown';
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {});

      const leadSourceData = Object.entries(leadSourceCount).map(([source, count]) => ({
        name: source,
        value: count
      }));

      // Order status data
      const orderStatusCount = ordersData.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
      }, {});

      const orderStatusData = Object.entries(orderStatusCount).map(([status, count]) => ({
        name: status.replace('_', ' ').toUpperCase(),
        value: count
      }));

      // Follow-up status data
      const followUpStatusCount = followUpsData.reduce((acc, fu) => {
        const status = fu.status || 'scheduled';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const followUpStatusData = Object.entries(followUpStatusCount).map(([status, count]) => ({
        name: status.replace('_', ' ').toUpperCase(),
        value: count
      }));

      // Generate trend data (last 7 days)
      const leadsTrendData = [];
      const ordersTrendData = [];
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
        
        const dayLeads = leadsData.filter(lead => {
          const leadDate = new Date(lead.created_at);
          return leadDate >= dayStart && leadDate < dayEnd;
        }).length;
        
        const dayOrders = ordersData.filter(order => {
          const orderDate = new Date(order.conversion_date);
          return orderDate >= dayStart && orderDate < dayEnd;
        }).length;
        
        leadsTrendData.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          leads: dayLeads
        });
        
        ordersTrendData.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          orders: dayOrders
        });
      }

      // Get recent leads
      const recentLeads = leadsData
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      // Top sources
      const topSources = Object.entries(leadSourceCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 3)
        .map(([source, count]) => ({ source, count }));

      // Monthly growth calculation
      const lastMonthLeads = leadsData.filter(lead => {
        const leadDate = new Date(lead.created_at);
        return leadDate >= lastMonthStart && leadDate < thisMonthStart;
      }).length;
      
      const monthlyGrowth = lastMonthLeads > 0 ? 
        ((thisMonthLeads - lastMonthLeads) / lastMonthLeads * 100) : 
        (thisMonthLeads > 0 ? 100 : 0);

      setStats({
        // Leads Statistics
        totalLeads: leadsData.length,
        todayLeads,
        thisMonthLeads,
        thisWeekLeads,
        recentLeads,
        leadStatusData,
        leadSourceData,
        leadsTrendData,
        
        // Follow-ups Statistics
        totalFollowUps: followUpsData.length,
        todayFollowUps,
        overdueFollowUps,
        upcomingFollowUps,
        followUpStatusData,
        
        // Orders Statistics
        totalOrders: ordersData.length,
        todayOrders,
        thisMonthOrders,
        conversionRate,
        orderStatusData,
        ordersTrendData,
        
        // Performance Metrics
        avgResponseTime: 0, // TODO: Calculate based on lead response times
        topSources,
        monthlyGrowth
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error(`Error loading dashboard data: ${error.message}`);
      
      // Set empty stats on error
      setStats({
        totalLeads: 0,
        todayLeads: 0,
        thisMonthLeads: 0,
        thisWeekLeads: 0,
        recentLeads: [],
        leadStatusData: [],
        leadSourceData: [],
        leadsTrendData: [],
        totalFollowUps: 0,
        todayFollowUps: 0,
        overdueFollowUps: 0,
        upcomingFollowUps: 0,
        followUpStatusData: [],
        totalOrders: 0,
        todayOrders: 0,
        thisMonthOrders: 0,
        conversionRate: 0,
        orderStatusData: [],
        ordersTrendData: [],
        avgResponseTime: 0,
        topSources: [],
        monthlyGrowth: 0
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  const setupRealtimeSubscription = useCallback(() => {
    const subscription = supabase
      .channel('dashboard_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'leads',
          filter: `assigned_to=eq.${user.id}`
        },
        (payload) => {
          loadDashboardData();
        }
      )
      .subscribe((status) => {
        console.log('Dashboard real-time subscription status:', status);
      });

    // Store subscription for cleanup
    window.dashboardSubscription = subscription;
  }, [user.id, loadDashboardData]);

  const cleanupRealtimeSubscription = () => {
    if (window.dashboardSubscription) {
      console.log('Cleaning up dashboard real-time subscription');
      supabase.removeChannel(window.dashboardSubscription);
      window.dashboardSubscription = null;
    }
  };

  useEffect(() => {
    if (user) {
      loadDashboardData();
      setupRealtimeSubscription();
    }

    // Cleanup subscription on unmount
    return () => {
      if (user) {
        cleanupRealtimeSubscription();
      }
    };
  }, [user, loadDashboardData, setupRealtimeSubscription]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's what's happening with your CRM.</p>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Leads */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Leads</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalLeads}</p>
              <p className="text-xs text-gray-400">All time</p>
            </div>
          </div>
        </div>

        {/* Today's Leads */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Today's Leads</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.todayLeads}</p>
              <p className="text-xs text-gray-400">New today</p>
            </div>
          </div>
        </div>

        {/* This Month's Leads */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ArrowTrendingUpIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">This Month</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.thisMonthLeads}</p>
              <p className="text-xs text-gray-400">
                {stats.monthlyGrowth > 0 ? `+${stats.monthlyGrowth.toFixed(1)}%` : `${stats.monthlyGrowth.toFixed(1)}%`} growth
              </p>
            </div>
          </div>
        </div>

        {/* Conversion Rate */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.conversionRate.toFixed(1)}%</p>
              <p className="text-xs text-gray-400">Leads to orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Follow-ups and Orders Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Follow-ups */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Follow-ups</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalFollowUps}</p>
              <p className="text-xs text-gray-400">Scheduled</p>
            </div>
          </div>
        </div>

        {/* Today's Follow-ups */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Today's Follow-ups</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.todayFollowUps}</p>
              <p className="text-xs text-gray-400">Due today</p>
            </div>
          </div>
        </div>

        {/* Overdue Follow-ups */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Overdue</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.overdueFollowUps}</p>
              <p className="text-xs text-gray-400">Past due date</p>
            </div>
          </div>
        </div>

        {/* Total Orders */}
        <div className="card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShoppingBagIcon className="h-8 w-8 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Orders</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalOrders}</p>
              <p className="text-xs text-gray-400">All time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads Trend Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Leads Trend (Last 7 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.leadsTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="leads" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Orders Trend Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Orders Trend (Last 7 Days)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.ordersTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="orders" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Distribution Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Status Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Status</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.leadStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.leadStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Order Status Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Order Status</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.orderStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.orderStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Lead Sources */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Lead Sources</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.leadSourceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Debug Section - Remove this after testing */}
      <DashboardDebug user={user} />
      
      {/* Debug Info */}
      <div className="card p-4 bg-gray-50">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Debug Information</h3>
        <div className="text-xs text-gray-600 space-y-1">
          <p>Total Leads: {stats.totalLeads}</p>
          <p>Today's Leads: {stats.todayLeads}</p>
          <p>This Month's Leads: {stats.thisMonthLeads}</p>
          <p>Total Follow-ups: {stats.totalFollowUps}</p>
          <p>Total Orders: {stats.totalOrders}</p>
          <p>Conversion Rate: {stats.conversionRate.toFixed(1)}%</p>
        </div>
      </div>

      {/* Recent Leads */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Leads</h3>
        </div>
        <div className="overflow-hidden">
          {stats.recentLeads.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No leads found. Create your first lead to get started!
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {stats.recentLeads.map((lead) => (
                <li key={lead.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{lead.name || lead.company || 'Unnamed Lead'}</p>
                      <p className="text-sm text-gray-500">{lead.email || lead.phone || lead.notes || 'No contact info'}</p>
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        lead.status === 'new' ? 'bg-green-100 text-green-800' :
                        lead.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        lead.status === 'qualified' ? 'bg-blue-100 text-blue-800' :
                        lead.status === 'lost' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {lead.status || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
